import { useCallback, useEffect, useMemo, useState } from 'react';

export const PREPARATION_PIPELINES = ['size', 'encapsulation'];

export function isCurrentMeasurement(measurement, state) {
  return Boolean(state && Number.isInteger(state.current_cycle_number) &&
    measurement.pipeline === state.pipeline &&
    measurement.cycle_number === state.current_cycle_number);
}

// Draft scope retains its captured revision; refreshing state must not bless a
// stale proposal with a newer revision. Future submission must revalidate it.
export function usePipelinePreparation({ batchId, batch, measurements, pipeline }) {
  const states = useMemo(() => String(batch?.id) === String(batchId) ? batch?.pipeline_preparation || [] : [], [batch, batchId]);
  const state = states.find((item) => item.pipeline === pipeline) || null;
  const currentMeasurements = useMemo(
    () => measurements.filter((item) => isCurrentMeasurement(item, state)),
    [measurements, state]
  );
  const historyMeasurements = measurements.filter((item) =>
    !states.some((scope) => isCurrentMeasurement(item, scope)));
  const [drafts, setDrafts] = useState({});
  const [notices, setNotices] = useState({});
  const [reloadRequired, setReloadRequired] = useState({});
  const noticeKey = JSON.stringify([batchId, pipeline]);
  const setPreparationNotice = useCallback((notice) => setNotices((previous) => ({ ...previous, [noticeKey]: notice })), [noticeKey]);
  const requirePreparationReload = useCallback(() => setReloadRequired((previous) => ({ ...previous, [noticeKey]: true })), [noticeKey]);
  // Invalidate only scopes whose authoritative revision has advanced. A proposal
  // may arrive before the Batch refresh carrying that same revision.
  useEffect(() => {
    setDrafts((previous) => {
      let next = previous;
      Object.entries(previous).forEach(([scopeKey, entry]) => {
        const [owner, selected, cycle] = JSON.parse(scopeKey);
        const current = states.find((item) => item.pipeline === selected);
        if (String(owner) === String(batchId) && current && entry.grouping &&
            (cycle !== current.current_cycle_number || current.preparation_revision > entry.grouping.preparation_revision)) {
          if (next === previous) next = { ...previous };
          next[scopeKey] = { ...entry, grouping: null, expanded: {} };
        }
      });
      return next;
    });
  }, [batchId, states, drafts]);
  const key = JSON.stringify([batchId, pipeline, state?.current_cycle_number ?? null]);
  const draft = drafts[key] || {};
  const setField = useCallback((field, value) => {
    setDrafts((previous) => {
      const entry = previous[key] || {};
      return { ...previous, [key]: { ...entry,
        [field]: typeof value === 'function' ? value(entry[field] ?? (field === 'grouping' ? null : field === 'importErrors' ? [] : {})) : value,
      } };
    });
  }, [key]);
  const setGroupingDraft = useCallback((value) => setField('grouping', value), [setField]);
  const setExpandedGroupingClusters = useCallback((value) => setField('expanded', value), [setField]);
  const setImportErrors = useCallback((value) => setField('importErrors', value), [setField]);
  return { preparationReloadRequired: Boolean(reloadRequired[noticeKey]), requirePreparationReload, preparationNotice: notices[noticeKey] || null, setPreparationNotice, importErrors: draft.importErrors || [], setImportErrors, state, currentMeasurements, historyMeasurements,
    groupingDraft: draft.grouping || null, setGroupingDraft,
    expandedGroupingClusters: draft.expanded || {}, setExpandedGroupingClusters };
}
